//#region libraries
import { Fragment, useEffect, useMemo, useState } from 'react';

// custom components
import { Card, CustomSpinner, DateTime } from '_atoms';

// bootstrap
import Badge from 'react-bootstrap/Badge';
import Button from 'react-bootstrap/Button';
import Col from 'react-bootstrap/Col';
import Form from 'react-bootstrap/Form';
import Modal from 'react-bootstrap/Modal';
import Row from 'react-bootstrap/Row';
import Table from 'react-bootstrap/Table';

// redux
import { useDispatch, useSelector } from 'react-redux';
import { selectDateToISO, setDate } from '_redux/slices/prm-bookings/calendar';
import { selectFirebaseUser, selectIata } from '_redux/slices/main-screen/user';

// services
import * as BookingService from '_services/booking-service';
import * as TimelineService from '_services/timeline-service';

// utils
import { format } from 'date-fns';
import {
    MATCH_LEVELS,
    assistanceCodes,
    buildMergePatch,
    getSource,
    groupBookings,
    isPartBooking,
    mergeBlocker,
    operationalFields,
    rankBookings,
} from '_utils/duplicate-bookings';

// tremor librarues
import { Subtitle, Text, Title, Toggle, ToggleItem } from '@tremor/react';
//#endregion

//#region components
const LIMIT = 1000;

const LEVEL_STYLES = {
    [MATCH_LEVELS.LIKELY]: { label: 'Likely duplicate', bg: 'danger', row: '#fdecea' },
    [MATCH_LEVELS.POSSIBLE]: { label: 'Possible duplicate', bg: 'warning', row: '#fff8e1' },
};

const flightTime = ({ booking, direction }) => {
    const f = booking.flightConfirmationDetails || {};
    const value = direction === 'Arrival' ? f.arrivalTime : f.departureTime;
    const date = value ? new Date(value) : null;
    return date && !isNaN(date.getTime()) ? date : null;
};

const flightLabel = (booking) => {
    const f = booking.flightConfirmationDetails || {};
    return `${f.carrierCode || ''}${f.flightNumber || ''}`;
};

const routeLabel = (booking) => {
    const f = booking.flightConfirmationDetails || {};
    return `${f.origin || '?'} → ${f.destination || '?'}`;
};

const createdLabel = (booking) => {
    const date = booking.dateCreated ? new Date(booking.dateCreated) : null;
    return date && !isNaN(date.getTime()) ? format(date, 'dd MMM HH:mm') : '—';
};

// side-by-side rows shown in the merge modal
const COMPARE_FIELDS = [
    ['Name', (b) => b.name],
    ['Flight', (b) => flightLabel(b)],
    ['Route', (b) => routeLabel(b)],
    ['Source', (b) => getSource(b)],
    ['Assistance', (b) => assistanceCodes(b).join(', ') || '—'],
    ['Due time', (b) => b.dueTime || '—'],
    ['Reference', (b) => b.referenceNumber || '—'],
    ['Notes / bio', (b) => b.notes || b.bio || '—'],
    ['Handler activity', (b) => operationalFields(b).join(', ') || 'None'],
    ['Created', (b) => createdLabel(b)],
    ['Booking id', (b) => b._id],
];
//#endregion

const BookingsManager = () => {

    //#region local state
    const [rows, setRows] = useState([]);
    const [status, setStatus] = useState('idle');
    const [reload, setReload] = useState(0);
    const [view, setView] = useState('all');
    const [search, setSearch] = useState('');
    const [selected, setSelected] = useState([]);           // booking ids ticked for merge
    const [deleteTarget, setDeleteTarget] = useState(null); // row pending delete
    const [mergePair, setMergePair] = useState(null);       // [a, b] rows pending merge
    const [keepId, setKeepId] = useState(null);
    const [saving, setSaving] = useState(false);
    const [actionError, setActionError] = useState('');
    //#endregion

    //#region redux
    const dispatch = useDispatch();
    const analyticsDate = useSelector(selectDateToISO);
    const iata = useSelector(selectIata);
    const firebaseUser = useSelector(selectFirebaseUser);
    //#endregion

    //#region useEffect
    useEffect(() => {
        if (!iata || !analyticsDate) return;
        const controller = new AbortController();
        const params = { iata, start: analyticsDate, end: analyticsDate, limit: LIMIT, skip: 0, search: '' };

        setStatus('loading');
        setSelected([]);
        Promise.all([
            BookingService.getDepartures(params, { signal: controller.signal }),
            BookingService.getArrivals(params, { signal: controller.signal }),
        ])
            .then(([departures, arrivals]) => {
                setRows([
                    ...(departures || []).filter(Boolean).map((booking) => ({ booking, direction: 'Departure' })),
                    ...(arrivals || []).filter(Boolean).map((booking) => ({ booking, direction: 'Arrival' })),
                ]);
                setStatus('succeeded');
            })
            .catch(() => {
                if (!controller.signal.aborted) setStatus('failed');
            });

        return () => controller.abort();
    }, [iata, analyticsDate, reload]);
    //#endregion

    //#region derived data
    const groups = useMemo(() => {
        const grouped = groupBookings(rows);
        const earliest = (group) => Math.min(...group.rows.map((row) => flightTime(row)?.getTime() ?? Infinity));
        return grouped.sort((a, b) => earliest(a) - earliest(b));
    }, [rows]);

    const flaggedCount = groups.filter((group) => group.level).length;

    const visibleGroups = useMemo(() => {
        const term = search.trim().toLowerCase();
        return groups.filter((group) => {
            if (view === 'duplicates' && !group.level) return false;
            if (!term) return true;
            return group.rows.some(({ booking }) =>
                (booking.name || '').toLowerCase().includes(term) || flightLabel(booking).toLowerCase().includes(term)
            );
        });
    }, [groups, view, search]);

    const rowById = useMemo(() => new Map(rows.map((row) => [row.booking._id, row])), [rows]);
    //#endregion

    //#region functions
    const auditUser = () => ({
        id: firebaseUser?.uid ?? '',
        name: firebaseUser?.displayName ?? '',
    });

    const toggleSelected = (id) => {
        setSelected((current) =>
            current.includes(id) ? current.filter((x) => x !== id) : [...current, id].slice(-2)
        );
    };

    const openMerge = (a, b) => {
        const [keep] = rankBookings([a.booking, b.booking]);
        setMergePair([a, b]);
        setKeepId(keep._id);
        setActionError('');
    };

    // soft delete — same path as the timeline: sets ignore:true with who/when,
    // clears the task and Locations entry
    const softDelete = (booking) => {
        const user = auditUser();
        return TimelineService.deleteTimelineJob({
            jobId: booking._id,
            body: { iata, deletedById: user.id, deletedByName: user.name },
        });
    };

    const confirmDelete = async () => {
        setSaving(true);
        setActionError('');
        try {
            await softDelete(deleteTarget.booking);
            setDeleteTarget(null);
            setReload((n) => n + 1);
        } catch (e) {
            setActionError('Could not delete the booking. Try again.');
        }
        setSaving(false);
    };

    const confirmMerge = async () => {
        const { keep, remove, blocker } = mergeSides;
        if (blocker) return;

        setSaving(true);
        setActionError('');
        const user = auditUser();
        try {
            // patchBooking/:id merges server-side and sends no passenger emails
            await BookingService.updateBooking({
                id: keep._id,
                body: buildMergePatch(keep, remove, { mergedById: user.id, mergedByName: user.name }),
            });
        } catch (e) {
            setActionError('Could not update the booking being kept. Nothing was changed.');
            setSaving(false);
            return;
        }
        try {
            await softDelete(remove);
            setMergePair(null);
            setSelected([]);
            setReload((n) => n + 1);
        } catch (e) {
            setActionError(`Details were copied to the kept booking, but ${remove.name} (${remove._id}) was not removed. Delete it from the list.`);
            setReload((n) => n + 1);
        }
        setSaving(false);
    };
    //#endregion

    //#region render helpers
    const mergeSides = useMemo(() => {
        if (!mergePair) return null;
        const [a, b] = mergePair;
        const keep = a.booking._id === keepId ? a.booking : b.booking;
        const remove = keep === a.booking ? b.booking : a.booking;
        return { keep, remove, blocker: mergeBlocker(keep, remove) };
    }, [mergePair, keepId]);

    const selectedRows = selected.map((id) => rowById.get(id)).filter(Boolean);

    const renderRow = (row, group) => {
        const { booking, direction } = row;
        const time = flightTime(row);
        const handled = operationalFields(booking).length > 0;
        return (
            <tr key={booking._id} style={{ backgroundColor: group.level ? LEVEL_STYLES[group.level].row : undefined }}>
                <td>
                    <Form.Check
                        type="checkbox"
                        checked={selected.includes(booking._id)}
                        disabled={isPartBooking(booking)}
                        onChange={() => toggleSelected(booking._id)}
                    />
                </td>
                <td>
                    {booking.name}
                    {isPartBooking(booking) && <Badge bg="secondary" className="ms-2">Part</Badge>}
                </td>
                <td>{direction}</td>
                <td>{flightLabel(booking)}</td>
                <td>{routeLabel(booking)}</td>
                <td>{time ? format(time, 'HH:mm') : '—'}</td>
                <td>{getSource(booking)}</td>
                <td>{assistanceCodes(booking).join(', ') || '—'}</td>
                <td>{handled ? <Badge bg="success">Handled</Badge> : '—'}</td>
                <td>{createdLabel(booking)}</td>
                <td>
                    <Button size="sm" variant="outline-danger" onClick={() => { setDeleteTarget(row); setActionError(''); }}>
                        Delete
                    </Button>
                </td>
            </tr>
        );
    };
    //#endregion

    return (
        <>
            {/* Controls */}
            <Row className="mt-2 align-items-center">
                <Col xs="auto">
                    <Toggle color="zinc" defaultValue={view} onValueChange={(value) => setView(value)}>
                        <ToggleItem value={'all'} text={`All passengers (${rows.length})`} />
                        <ToggleItem value={'duplicates'} text={`Potential duplicates (${flaggedCount})`} />
                    </Toggle>
                </Col>
                <Col>
                    <Form.Control
                        placeholder="Search name or flight"
                        value={search}
                        onChange={(e) => setSearch(e.target.value)}
                    />
                </Col>
                <Col xs="auto">
                    <Button
                        variant="primary"
                        disabled={selectedRows.length !== 2}
                        onClick={() => openMerge(selectedRows[0], selectedRows[1])}
                    >
                        Merge selected ({selectedRows.length}/2)
                    </Button>
                </Col>
                <Col xs="auto">
                    <DateTime isBooking={true} date={analyticsDate} setDate={(date) => dispatch(setDate(date))} />
                </Col>
            </Row>

            <Card className="mt-4">
                <Title>Bookings Manager</Title>
                <Subtitle>
                    Similar bookings are grouped together and flagged. Tick two bookings to merge them, or delete one.
                </Subtitle>

                {status === 'loading' && <CustomSpinner rowCenter={true} />}
                {status === 'failed' && <Text marginTop="mt-4">Could not load bookings for {analyticsDate}.</Text>}
                {status === 'succeeded' && rows.length >= LIMIT && (
                    <Text marginTop="mt-4">Showing the first {LIMIT} bookings per direction — some may be missing.</Text>
                )}

                {status === 'succeeded' && (
                    visibleGroups.length === 0 ? (
                        <Text marginTop="mt-4">
                            {view === 'duplicates' ? 'No potential duplicates for this day.' : 'No bookings found.'}
                        </Text>
                    ) : (
                        <Table responsive hover size="sm" className="mt-4">
                            <thead>
                                <tr>
                                    <th></th>
                                    <th>Passenger</th>
                                    <th>Direction</th>
                                    <th>Flight</th>
                                    <th>Route</th>
                                    <th>Time</th>
                                    <th>Source</th>
                                    <th>Assistance</th>
                                    <th>Status</th>
                                    <th>Created</th>
                                    <th></th>
                                </tr>
                            </thead>
                            <tbody>
                                {visibleGroups.map((group) => (
                                    <Fragment key={group.id}>
                                        {group.level && (
                                            <tr>
                                                <td colSpan={11} style={{ backgroundColor: LEVEL_STYLES[group.level].row }}>
                                                    <Badge bg={LEVEL_STYLES[group.level].bg} text={group.level === MATCH_LEVELS.POSSIBLE ? 'dark' : undefined}>
                                                        {LEVEL_STYLES[group.level].label}
                                                    </Badge>
                                                    <span className="ms-2">{group.reasons.join(' · ')}</span>
                                                    {group.rows.length === 2 && (
                                                        <Button
                                                            size="sm"
                                                            variant="outline-primary"
                                                            className="ms-3"
                                                            onClick={() => openMerge(group.rows[0], group.rows[1])}
                                                        >
                                                            Merge
                                                        </Button>
                                                    )}
                                                </td>
                                            </tr>
                                        )}
                                        {group.rows.map((row) => renderRow(row, group))}
                                    </Fragment>
                                ))}
                            </tbody>
                        </Table>
                    )
                )}
            </Card>

            {/* Delete Modal */}
            <Modal show={!!deleteTarget} onHide={() => !saving && setDeleteTarget(null)} centered>
                <Modal.Header closeButton>
                    <Modal.Title>Delete booking</Modal.Title>
                </Modal.Header>
                <Modal.Body>
                    {deleteTarget && (
                        <>
                            <p>
                                Delete <strong>{deleteTarget.booking.name}</strong> on {flightLabel(deleteTarget.booking)} ({deleteTarget.direction.toLowerCase()})?
                            </p>
                            <p className="mb-0">
                                The booking is removed from the lists and the timeline. It is kept on record with your name and the time.
                            </p>
                            {operationalFields(deleteTarget.booking).length > 0 && (
                                <p className="mt-2 mb-0 text-danger">
                                    This booking has handler activity ({operationalFields(deleteTarget.booking).join(', ')}).
                                </p>
                            )}
                        </>
                    )}
                    {actionError && <p className="mt-2 mb-0 text-danger">{actionError}</p>}
                </Modal.Body>
                <Modal.Footer>
                    <Button variant="secondary" disabled={saving} onClick={() => setDeleteTarget(null)}>Cancel</Button>
                    <Button variant="danger" disabled={saving} onClick={confirmDelete}>
                        {saving ? 'Deleting…' : 'Delete'}
                    </Button>
                </Modal.Footer>
            </Modal>

            {/* Merge Modal */}
            <Modal show={!!mergePair} onHide={() => !saving && setMergePair(null)} size="lg" centered>
                <Modal.Header closeButton>
                    <Modal.Title>Merge bookings</Modal.Title>
                </Modal.Header>
                <Modal.Body>
                    {mergePair && (() => {
                        const [a, b] = mergePair;
                        const { blocker } = mergeSides;
                        const sameGroup = groups.some((group) =>
                            group.level && group.rows.includes(a) && group.rows.includes(b)
                        );
                        return (
                            <>
                                <p>
                                    Choose the booking to keep. Blank details on it are filled from the other booking, then the other booking is deleted.
                                </p>
                                {!sameGroup && (
                                    <p className="text-danger">
                                        These bookings are not flagged as duplicates — check they are the same passenger before merging.
                                    </p>
                                )}
                                <Table bordered size="sm">
                                    <thead>
                                        <tr>
                                            <th></th>
                                            {[a, b].map((row) => (
                                                <th key={row.booking._id}>
                                                    <Form.Check
                                                        type="radio"
                                                        name="keep-booking"
                                                        label={row.booking._id === keepId ? 'Keep' : 'Remove'}
                                                        checked={row.booking._id === keepId}
                                                        onChange={() => setKeepId(row.booking._id)}
                                                    />
                                                </th>
                                            ))}
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {COMPARE_FIELDS.map(([label, value]) => (
                                            <tr key={label}>
                                                <td><strong>{label}</strong></td>
                                                <td style={{ wordBreak: 'break-all' }}>{value(a.booking)}</td>
                                                <td style={{ wordBreak: 'break-all' }}>{value(b.booking)}</td>
                                            </tr>
                                        ))}
                                    </tbody>
                                </Table>
                                {blocker && <p className="mb-0 text-danger">{blocker}</p>}
                            </>
                        );
                    })()}
                    {actionError && <p className="mt-2 mb-0 text-danger">{actionError}</p>}
                </Modal.Body>
                <Modal.Footer>
                    <Button variant="secondary" disabled={saving} onClick={() => setMergePair(null)}>Cancel</Button>
                    <Button
                        variant="primary"
                        disabled={saving || !mergeSides || !!mergeSides.blocker}
                        onClick={confirmMerge}
                    >
                        {saving ? 'Merging…' : 'Merge'}
                    </Button>
                </Modal.Footer>
            </Modal>
        </>
    );
};

export default BookingsManager;
